// Cache-invalidation helpers for thread queries.
//
// Send/delete/title-generation flows call these instead of poking
// query keys directly so the "what to bust on what action" rule
// lives in one file. Each helper is a thin wrapper so unit tests can
// assert which keys were invalidated.

import type { QueryClient } from '@tanstack/react-query';
import { threadKeys } from './queryKeys';

export function invalidateThreadList(client: QueryClient): Promise<void> {
  // `predicate` covers any limit variant of threads.list(limit).
  return client.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return Array.isArray(k) && k[0] === 'threads' && k[1] === 'list';
    },
  });
}

export function invalidateThreadDetail(
  client: QueryClient,
  threadId: string,
): Promise<void> {
  return client.invalidateQueries({ queryKey: threadKeys.detail(threadId) });
}

export function invalidatePromptedAgentIds(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: threadKeys.promptedAgents() });
}

/** Bust both the list and a specific thread (e.g. after send-message). */
export function invalidateThread(
  client: QueryClient,
  threadId: string,
): Promise<void> {
  return Promise.all([
    invalidateThreadList(client),
    invalidatePromptedAgentIds(client),
    invalidateThreadDetail(client, threadId),
  ]).then(() => undefined);
}

/** Bust everything threads-related (e.g. after sign-out or agent switch). */
export function invalidateAllThreads(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: threadKeys.all() });
}
